/*
 * ReminderNotes — the "add a note" composer + notes thread (inline edit /
 * delete) for a single reminder. Extracted from ReminderDetailsModal so the
 * same notes UI can be reused inside the edit form without duplicating logic.
 *
 * Keeps its own copy of the notes list seeded from the `notes` prop so a
 * mutation reflects immediately; every write also bubbles the fresh task up
 * via `onUpdated` so the host (details modal / edit modal) can refresh its
 * list. Notes only attach to a saved reminder, so this renders nothing without
 * a `taskUuid`.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { FaRegStickyNote, FaUserCircle, FaRegEdit, FaRegTrashAlt } from 'react-icons/fa';

import { getCookie } from '../../../util_functions';
import { current_team_member, add_note, update_note, delete_note } from './api';
import FormattedNoteText from './FormattedNoteText';
import './reminders.css';

function ReminderNotes({ taskUuid, notes: notesProp, onUpdated, tooltipId }) {
  const [notes, setNotes] = useState(notesProp || []);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Per-note edit state: which note is being edited, and the editable text.
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingDraft, setEditingDraft] = useState('');
  const cms_email = getCookie('cms_email');
  const me = current_team_member(cms_email);

  // Reset transient state and re-seed the list whenever we switch tasks.
  useEffect(() => {
    setNotes(notesProp || []);
    setDraft('');
    setEditingNoteId(null);
    setEditingDraft('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskUuid]);

  if (!taskUuid) return null;

  // Reflect a write locally (so the thread updates without a round-trip) and
  // bubble the fresh task up so the host list can refresh.
  const applyUpdated = (updated) => {
    if (!updated) return;
    setNotes(updated.notes || []);
    if (onUpdated) onUpdated(updated);
  };

  const handleAddNote = () => {
    const text = draft.trim();
    if (!text) return;
    setSubmitting(true);
    add_note(taskUuid, text, me?.name)
      .then((updated) => {
        setDraft('');
        applyUpdated(updated);
      })
      .finally(() => setSubmitting(false));
  };

  const handleKeyDown = (e) => {
    // Cmd/Ctrl + Enter to submit.
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleAddNote();
    }
  };

  const handleStartEditNote = (n) => {
    setEditingNoteId(n.uuid);
    setEditingDraft(n.text || '');
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingDraft('');
  };

  const handleSaveEditNote = (n) => {
    const text = editingDraft.trim();
    if (!text || text === n.text) {
      handleCancelEditNote();
      return;
    }
    update_note(taskUuid, n.uuid, text).then((updated) => {
      handleCancelEditNote();
      applyUpdated(updated);
    });
  };

  // Immediate delete — single click removes the note. The list refreshes
  // through `applyUpdated` so the row disappears as soon as the stub resolves.
  const handleDeleteNote = (n) => {
    delete_note(taskUuid, n.uuid).then(applyUpdated);
  };

  return (
    <>
      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-content-muted">
        <FaRegStickyNote className="text-content-muted" size={11} />
        Add a note
      </div>
      <textarea
        rows={6}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="What did you do / observe? (⌘/Ctrl + Enter to save)"
        style={{
          border: '1.5px solid var(--border-default)',
          borderRadius: 6,
          width: '100%',
          minHeight: 140,
          padding: '10px 12px',
          fontSize: 14,
          color: 'var(--text-primary)',
          background: 'var(--surface-card)',
          outline: 'none',
          resize: 'vertical',
          display: 'block'
        }}
      />
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={handleAddNote}
          disabled={submitting || !draft.trim()}
          className="reminders-primary-btn text-sm font-medium"
        >
          {submitting ? 'Adding…' : 'Add note'}
        </button>
      </div>

      <div className="mt-4 mb-1 text-[11px] font-semibold uppercase tracking-wider text-content-muted">
        Notes ({notes.length})
      </div>
      <div className="-mx-1 flex-1 space-y-2 overflow-y-auto px-1">
        {notes.length === 0 ? (
          <div className="rounded-md border border-dashed border-line py-4 text-center text-xs text-content-muted">
            No notes yet. Add the first one above.
          </div>
        ) : (
          notes.map((n) => {
            const editing = editingNoteId === n.uuid;
            return (
              <div
                key={n.uuid}
                className="group rounded-md border border-line bg-surface-card px-3 py-2 text-sm text-content-primary shadow-sm"
              >
                {editing ? (
                  <>
                    <textarea
                      autoFocus
                      value={editingDraft}
                      onChange={(e) => setEditingDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                          e.preventDefault();
                          handleSaveEditNote(n);
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          handleCancelEditNote();
                        }
                      }}
                      rows={6}
                      style={{
                        border: '1.5px solid var(--border-default)',
                        borderRadius: 6,
                        width: '100%',
                        minHeight: 140,
                        padding: '10px 12px',
                        fontSize: 14,
                        color: 'var(--text-primary)',
                        background: 'var(--surface-card)',
                        outline: 'none',
                        resize: 'vertical',
                        display: 'block'
                      }}
                    />
                    <div className="mt-2 flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={handleCancelEditNote}
                        className="reminders-secondary-btn text-xs"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSaveEditNote(n)}
                        disabled={!editingDraft.trim()}
                        className="reminders-primary-btn text-xs font-medium"
                      >
                        Save
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <FormattedNoteText text={n.text} />
                      <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() => handleStartEditNote(n)}
                          data-tooltip-id={tooltipId}
                          data-tooltip-content="Edit note"
                          title="Edit note"
                          aria-label="Edit note"
                          className="reminders-icon-btn"
                        >
                          <FaRegEdit size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteNote(n)}
                          data-tooltip-id={tooltipId}
                          data-tooltip-content="Delete note"
                          title="Delete note"
                          aria-label="Delete note"
                          className="reminders-icon-btn"
                        >
                          <FaRegTrashAlt size={12} />
                        </button>
                      </div>
                    </div>
                    <div className="mt-1 flex items-center justify-between text-[10px] text-content-muted">
                      <span className="inline-flex items-center gap-1">
                        <FaUserCircle size={9} className="text-content-muted" />
                        {n.author || 'Unknown'}
                      </span>
                      <span title={moment(n.created_at).format('llll')}>
                        {moment(n.created_at).fromNow()}
                        {n.edited_at ? (
                          <span className="ml-1 text-content-muted">(edited)</span>
                        ) : null}
                      </span>
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}
      </div>
    </>
  );
}

ReminderNotes.propTypes = {
  taskUuid: PropTypes.string,
  notes: PropTypes.arrayOf(PropTypes.object),
  onUpdated: PropTypes.func,
  tooltipId: PropTypes.string
};

export default ReminderNotes;
